<template>
  <transition name="fade">
    <div
      v-if="show"
      class="fixed inset-0 bg-gray-900 bg-opacity-80 flex items-center justify-center z-50"
    >
      <div class="bg-gray-800 p-6 rounded-lg border border-gray-700 shadow-xl w-full max-w-md">
        <div class="flex items-center gap-2 mb-4">
          <i class="fas fa-gem text-white text-lg"></i>
          <h3 class="text-lg font-semibold text-white">จัดการเพชร</h3>
        </div>
        <p class="text-sm text-gray-400 mb-4">ฝากหรือถอนเพชรจากคลัง (ฝากสูงสุด 100 เพชรต่ออาทิตย์)</p>
        <div class="bg-gray-800 border border-gray-700 p-3 rounded-lg mb-4">
          <p class="text-sm text-gray-400 flex items-center gap-2">
            <i class="fas fa-gem text-blue-300 text-sm"></i> เพชรในตัวละคร: {{ diamond }}
          </p>
          <p class="text-sm text-gray-400 flex items-center gap-2">
            <i class="fas fa-gem text-blue-300 text-sm"></i> เพชรในคลัง: {{ diamondStored }}
          </p>
          <p class="text-sm text-gray-400 flex items-center gap-2">
            <i class="fas fa-gem text-blue-300 text-sm"></i> ฝากอาทิตย์นี้: {{ weeklyDepositedDiamond }} / 100
          </p>
        </div>
        <div class="mb-4">
          <p class="text-sm text-gray-400 mb-2">เลือกการกระทำ:</p>
          <div class="flex gap-4">
            <label class="flex items-center gap-2">
              <input
                type="radio"
                v-model="diamondType"
                value="deposit"
                class="text-indigo-500 focus:ring-indigo-500"
              />
              <span class="text-sm text-white">ฝาก</span>
            </label>
            <label class="flex items-center gap-2">
              <input
                type="radio"
                v-model="diamondType"
                value="withdraw"
                class="text-indigo-500 focus:ring-indigo-500"
              />
              <span class="text-sm text-white">ถอน</span>
            </label>
          </div>
        </div>
        <div v-if="diamondType === 'deposit'" class="mb-4">
          <label class="text-sm text-gray-400 mb-2 block">จำนวนเพชรที่ต้องการฝาก:</label>
          <input
            v-model="localDepositAmount"
            type="number"
            min="1"
            :max="maxDepositAmount"
            step="1"
            class="bg-gray-700 text-white rounded-lg p-2 w-full focus:outline-none focus:ring-2 focus:ring-indigo-500 transition-all duration-200"
          />
          <div
            v-if="localDepositAmount > maxDepositAmount"
            class="text-yellow-300 mt-2 flex items-center gap-2 text-sm"
          >
            <i class="fas fa-exclamation-triangle text-yellow-300 text-sm"></i> เกินขีดจำกัดอาทิตย์นี้!
          </div>
          <div
            v-else-if="localDepositAmount > diamond"
            class="text-red-300 mt-2 flex items-center gap-2 text-sm"
          >
            <i class="fas fa-exclamation-circle text-red-300 text-sm"></i> เพชรไม่เพียงพอ!
          </div>
        </div>
        <div v-if="diamondType === 'withdraw'" class="mb-4">
          <label class="text-sm text-gray-400 mb-2 block">จำนวนเพชรที่ต้องการถอน:</label>
          <input
            v-model="localWithdrawAmount"
            type="number"
            min="1"
            :max="diamondStored"
            step="1"
            class="bg-gray-700 text-white rounded-lg p-2 w-full focus:outline-none focus:ring-2 focus:ring-indigo-500 transition-all duration-200"
          />
          <div
            v-if="localWithdrawAmount > diamondStored"
            class="text-red-300 mt-2 flex items-center gap-2 text-sm"
          >
            <i class="fas fa-exclamation-circle text-red-300 text-sm"></i> เพชรในคลังไม่เพียงพอ!
          </div>
        </div>
        <div v-if="localError" class="text-red-300 mb-4 text-sm">{{ localError }}</div>
        <div class="flex justify-end space-x-2">
          <button
            @click="submitDiamondAction"
            class="py-2 px-4 bg-indigo-600 hover:bg-indigo-500 border border-indigo-700 text-white rounded-lg transition-all duration-200 flex items-center gap-2"
            :class="{ 'bg-gray-600 opacity-0.6 cursor-not-allowed': isProcessing || isDiamondSubmitDisabled }"
            :disabled="isProcessing || isDiamondSubmitDisabled"
          >
            <span v-if="!isProcessing"><i class="fas fa-check text-sm"></i> ยืนยัน</span>
            <i v-else class="fas fa-spinner animate-spin text-indigo-200 text-sm"></i>
          </button>
          <button
            @click="closeModal"
            class="py-2 px-4 bg-gray-600 hover:bg-gray-500 border border-gray-600 text-white rounded-lg transition-all duration-200 flex items-center gap-2"
          >
            <i class="fas fa-times text-sm"></i> ยกเลิก
          </button>
        </div>
      </div>
    </div>
  </transition>
</template>

<script>
import { ref, computed } from 'vue';
import { projectFunctions } from '@/firebase/config';

export default {
  props: {
    show: {
      type: Boolean,
      default: false
    },
    diamond: {
      type: Number,
      required: true
    },
    diamondStored: {
      type: Number,
      required: true
    },
    weeklyDepositedDiamond: {
      type: Number,
      required: true
    },
    error: {
      type: String,
      default: ''
    }
  },
  emits: ['update:show', 'update:error'],
  setup(props, { emit }) {
    const diamondType = ref('deposit');
    const localDepositAmount = ref(0);
    const localWithdrawAmount = ref(0);
    const isProcessing = ref(false);
    const localError = ref('');

    const maxDepositAmount = computed(() => 100 - props.weeklyDepositedDiamond);

    const isDiamondSubmitDisabled = computed(() => {
      if (diamondType.value === 'deposit') {
        return localDepositAmount.value <= 0 || localDepositAmount.value > maxDepositAmount.value || localDepositAmount.value > props.diamond;
      } else if (diamondType.value === 'withdraw') {
        return localWithdrawAmount.value <= 0 || localWithdrawAmount.value > props.diamondStored;
      }
      return true;
    });

    const submitDiamondAction = async () => {
      isProcessing.value = true;
      localError.value = '';
      try {
        const diamondManagement = projectFunctions.httpsCallable('gameActions');
        if (diamondType.value === 'deposit') {
          await diamondManagement({ action: 'diamondManagement', subAction: 'depositDiamond', amount: Math.floor(localDepositAmount.value) });
          localDepositAmount.value = 0;
        } else if (diamondType.value === 'withdraw') {
          await diamondManagement({ action: 'diamondManagement', subAction: 'withdrawDiamond', amount: Math.floor(localWithdrawAmount.value) });
          localWithdrawAmount.value = 0;
        }
        closeModal();
      } catch (err) {
        localError.value = err.message || 'เกิดข้อผิดพลาด';
        emit('update:error', localError.value);
      } finally {
        isProcessing.value = false;
      }
    };

    const closeModal = () => {
      emit('update:show', false);
      localDepositAmount.value = 0;
      localWithdrawAmount.value = 0;
      diamondType.value = 'deposit';
      localError.value = '';
    };

    return {
      diamondType,
      localDepositAmount,
      localWithdrawAmount,
      isProcessing,
      localError,
      maxDepositAmount,
      isDiamondSubmitDisabled,
      submitDiamondAction,
      closeModal
    };
  }
};
</script>

<style scoped>
/* Add any specific styles if needed */
</style>